import React, { useEffect, useRef, useState } from "react";
import { api, errText } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Sparkles, Camera, Wand2, X, Link as LinkIcon, Combine } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import AdStrip from "../components/AdStrip";

const CATEGORIES = ["top", "bottom", "dress", "outerwear", "footwear", "accessory"];

const Wardrobe = () => {
  const [url, setUrl] = useState("");
  const [category, setCategory] = useState("top");
  const [items, setItems] = useState(null);  // null while loading, [] when empty
  const [busy, setBusy] = useState(false);
  const [tagBusy, setTagBusy] = useState(false);

  // Screenshot upload state
  const [shotOpen, setShotOpen] = useState(false);
  const [shotImage, setShotImage] = useState(null);  // data URL
  const [shotTitle, setShotTitle] = useState("");
  const [shotCategory, setShotCategory] = useState("top");
  // Mode: "ai" (AI analyse main garment) | "quick" (user-supplied) | "multi" (pick individual pieces)
  const [shotMode, setShotMode] = useState("ai");
  const [shotBusy, setShotBusy] = useState(false);
  // Multi-item flow — detected pieces + which one is currently being saved
  const [detected, setDetected] = useState(null); // null | [] | [{label, category}]
  const [detectBusy, setDetectBusy] = useState(false);
  const [savedLabels, setSavedLabels] = useState({}); // { [label]: true }
  const [savingLabel, setSavingLabel] = useState(null);
  const shotFileRef = useRef(null);

  const load = async () => {
    const r = await api.get("/wardrobe"); setItems(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const retag = async () => {
    setTagBusy(true);
    try {
      const r = await api.post("/wardrobe/tag-untagged");
      if (r.data.tagged > 0) {
        toast.success(`Tagged ${r.data.tagged} of ${r.data.scanned} pieces`);
        await load();
      } else {
        toast("Wardrobe already tagged", { icon: "✓" });
      }
    } catch (err) { toast.error(errText(err, "Tagging failed")); }
    finally { setTagBusy(false); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/wardrobe/scrape", { url, category });
      setItems((p) => [r.data, ...p]);
      setUrl("");
      toast.success("Added to wardrobe");
    } catch (err) {
      toast.error(errText(err, "Could not import from URL"));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Remove this piece from your wardrobe?")) return;
    await api.delete(`/wardrobe/${id}`);
    setItems((p) => p.filter(x => x.id !== id));
  };

  // ---------- Merge duplicates ----------
  const [mergeKeepItem, setMergeKeepItem] = useState(null);
  const [mergePicked, setMergePicked] = useState({});   // { [id]: true }
  const [mergeBusy, setMergeBusy] = useState(false);

  const openMerge = (item) => {
    setMergeKeepItem(item);
    setMergePicked({});
  };
  const closeMerge = () => {
    setMergeKeepItem(null);
    setMergePicked({});
  };
  const toggleMergePick = (id) => {
    setMergePicked((p) => ({ ...p, [id]: !p[id] }));
  };
  const confirmMerge = async () => {
    if (!mergeKeepItem) return;
    const dupIds = Object.keys(mergePicked).filter((k) => mergePicked[k]);
    if (dupIds.length === 0) {
      toast.error("Pick at least one duplicate to merge");
      return;
    }
    setMergeBusy(true);
    try {
      const r = await api.post(`/wardrobe/${mergeKeepItem.id}/merge`, {
        duplicate_ids: dupIds,
      });
      // Drop merged docs from local state
      setItems((p) => p.filter((x) => !dupIds.includes(x.id)));
      toast.success(
        `Merged ${r.data.merged_ids.length} piece${r.data.merged_ids.length === 1 ? "" : "s"}` +
        (r.data.references_updated ? ` · ${r.data.references_updated} outfit/try-on updated` : "")
      );
      closeMerge();
    } catch (err) {
      toast.error(errText(err, "Merge failed"));
    } finally { setMergeBusy(false); }
  };

  const onShotFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      toast.error("Screenshot too large (max 4MB). Please compress and retry.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setShotImage(reader.result);
      // Reset multi-item detection state whenever a new image is dropped in
      setDetected(null);
      setSavedLabels({});
      setSavingLabel(null);
    };
    reader.readAsDataURL(f);
  };

  const closeShot = () => {
    setShotOpen(false); setShotImage(null); setShotTitle("");
    setShotCategory("top"); setShotMode("ai");
    setDetected(null); setSavedLabels({}); setSavingLabel(null);
  };

  const submitShot = async (e) => {
    e.preventDefault();
    if (!shotImage) { toast.error("Upload a screenshot first"); return; }
    if (shotMode === "multi") return;   // multi flow uses its own buttons
    setShotBusy(true);
    try {
      const r = await api.post("/wardrobe/screenshot", {
        image_data_url: shotImage,
        title: shotTitle || undefined,
        category: shotMode === "ai" ? undefined : shotCategory,
        analyse: shotMode === "ai",
      });
      setItems((p) => [r.data, ...(p || [])]);
      toast.success(shotMode === "ai" ? `Muse tagged: ${r.data.title}` : "Added to wardrobe");
      closeShot();
    } catch (err) {
      toast.error(errText(err, "Could not add screenshot"));
    } finally { setShotBusy(false); }
  };

  const runDetect = async () => {
    if (!shotImage) { toast.error("Upload a screenshot first"); return; }
    setDetectBusy(true);
    setDetected(null);
    try {
      const r = await api.post("/wardrobe/screenshot/detect", { image_data_url: shotImage });
      const list = r.data?.items || [];
      setDetected(list);
      if (list.length === 0) toast("No garments detected — try a clearer photo", { icon: "!" });
      else toast.success(`${list.length} piece${list.length === 1 ? "" : "s"} detected`);
    } catch (err) {
      toast.error(errText(err, "Detection failed"));
      setDetected([]);
    } finally { setDetectBusy(false); }
  };

  const savePickedItem = async (label, category) => {
    if (savingLabel) return;
    setSavingLabel(label);
    try {
      const r = await api.post("/wardrobe/screenshot", {
        image_data_url: shotImage,
        focus_item: { label, category },
      });
      setItems((p) => [r.data, ...(p || [])]);
      setSavedLabels((s) => ({ ...s, [label]: true }));
      toast.success(`Saved · ${r.data.title}`);
    } catch (err) {
      toast.error(errText(err, "Could not isolate this piece"));
    } finally { setSavingLabel(null); }
  };

  // Bulk isolate + save every remaining detected piece. Runs serially so we
  // don't hammer Gemini with 6 parallel image generations from the same user.
  const [bulkBusy, setBulkBusy] = useState(false);
  const saveAllPicked = async () => {
    if (bulkBusy || savingLabel) return;
    const remaining = (detected || []).filter((it) => !savedLabels[it.label]);
    if (remaining.length === 0) return;
    setBulkBusy(true);
    let ok = 0;
    let failed = 0;
    for (const it of remaining) {
      setSavingLabel(it.label);
      try {
        const r = await api.post("/wardrobe/screenshot", {
          image_data_url: shotImage,
          focus_item: { label: it.label, category: it.category },
        });
        setItems((p) => [r.data, ...(p || [])]);
        setSavedLabels((s) => ({ ...s, [it.label]: true }));
        ok += 1;
      } catch (err) {
        failed += 1;
        // Stop on quota / 429 to avoid wasted attempts and to surface upgrade CTA
        const status = err?.response?.status;
        if (status === 429 || status === 402) {
          toast.error(errText(err, "Daily cap reached"));
          break;
        }
      }
    }
    setSavingLabel(null);
    setBulkBusy(false);
    if (ok > 0 && failed === 0) toast.success(`Saved all ${ok} piece${ok === 1 ? "" : "s"}`);
    else if (ok > 0 && failed > 0) toast(`Saved ${ok} · ${failed} failed`);
    else if (ok === 0 && failed > 0) toast.error("Could not save any pieces");
  };

  const grouped = CATEGORIES.reduce((acc, c) => ({ ...acc, [c]: (items || []).filter(i => i.category === c) }), {});

  return (
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
      {/* Heading */}
      <div className="border-b border-black/10 pb-8 mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono-eyebrow">Cross-store wardrobe</div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-none mt-2">Import from anywhere.</h1>
          <p className="text-black/60 mt-4 max-w-2xl">
            Two ways in. Paste a shopping link, or drop a screenshot when the site
            blocks direct imports. Either path gives you a wardrobe piece you can
            fit onto your portrait.
          </p>
        </div>
        <button type="button" data-testid="wardrobe-retag-btn" onClick={retag}
          disabled={tagBusy || !items || items.length===0}
          className="editorial-link font-mono-eyebrow flex items-center gap-1.5 disabled:opacity-40">
          <Sparkles className="w-3.5 h-3.5"/> {tagBusy ? "Tagging…" : "Retag with Muse"}
        </button>
      </div>

      {/* Dual-import panel */}
      <div className="grid grid-cols-12 gap-8 mb-14">
        {/* URL path */}
        <div className="col-span-12 lg:col-span-6 bg-white border border-black/10 p-8 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-mono-eyebrow text-[10px]">01</div>
            <div className="font-mono-eyebrow">From a link</div>
          </div>
          <h2 className="font-serif-display text-3xl leading-tight">Paste any product URL.</h2>
          <p className="text-black/60 text-sm mt-2 leading-relaxed">
            Works with SSENSE, H&M, Uniqlo, Farfetch, most Shopify stores, and
            many others.
          </p>
          <form onSubmit={add} className="mt-6 flex flex-col gap-4 flex-1">
            <div>
              <label className="font-mono-eyebrow text-black/50 text-[10px]">Product URL</label>
              <input
                data-testid="wardrobe-url-input"
                value={url} onChange={(e)=>setUrl(e.target.value)}
                placeholder="https://www.ssense.com/en-us/…"
                className="field mt-2"
              />
            </div>
            <div>
              <label className="font-mono-eyebrow text-black/50 text-[10px]">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="wardrobe-cat-select" className="w-full rounded-none border-0 border-b border-black bg-transparent font-mono-eyebrow uppercase focus:ring-0 mt-1">
                  <SelectValue/>
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c} className="font-mono-eyebrow">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-auto pt-4">
              <button data-testid="wardrobe-add-btn" type="submit" disabled={busy}
                className="btn-ink w-full flex items-center justify-center gap-2 disabled:opacity-50">
                <LinkIcon className="w-3.5 h-3.5"/> {busy ? "Importing…" : "Import from URL"}
              </button>
            </div>
          </form>
        </div>

        {/* Image / screenshot path */}
        <div className="col-span-12 lg:col-span-6 bg-white border border-black/10 p-8 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-[color:var(--accent)] text-white flex items-center justify-center font-mono-eyebrow text-[10px]">02</div>
            <div className="font-mono-eyebrow">From an image</div>
          </div>
          <h2 className="font-serif-display text-3xl leading-tight">Upload a screenshot.</h2>
          <p className="text-black/60 text-sm mt-2 leading-relaxed">
            Some shops (Zara, Amazon, most iOS apps) block direct imports.
            Screenshot the garment — Muse reads the piece, tags it, and files it.
          </p>
          <div className="mt-6 flex-1 flex flex-col justify-end">
            <div className="flex items-center gap-6 mb-4 text-[11px] font-mono-eyebrow text-black/50">
              <span className="flex items-center gap-1.5"><Wand2 className="w-3 h-3"/> AI reads & tags</span>
              <span className="flex items-center gap-1.5"><Camera className="w-3 h-3"/> Or quick add</span>
              <span className="flex items-center gap-1.5"><Sparkles className="w-3 h-3"/> Works in try-on</span>
            </div>
            <button type="button" data-testid="wardrobe-shot-btn" onClick={() => setShotOpen(true)}
              className="btn-outline-ink w-full flex items-center justify-center gap-2">
              <Camera className="w-3.5 h-3.5"/> Upload garment image
            </button>
          </div>
        </div>
      </div>

      {!items ? (
        <div className="py-24 text-center font-mono-eyebrow text-black/40">Loading wardrobe…</div>
      ) : items.length === 0 ? (
        <div className="py-24 text-center">
          <p className="font-serif-display text-4xl">Nothing hangs here yet.</p>
          <p className="text-black/60 mt-2 font-mono-eyebrow">Try a link from Zara, H&M, ASOS, SSENSE, or any store.</p>
        </div>
      ) : (
        <div className="mt-12 space-y-16">
          {CATEGORIES.map(cat => grouped[cat].length > 0 && (
            <section key={cat}>
              <div className="flex items-baseline gap-6 mb-6">
                <h2 className="font-serif-display text-4xl capitalize">{cat}s</h2>
                <span className="font-mono-eyebrow text-black/40">{grouped[cat].length} pieces</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                {grouped[cat].map(it => (
                  <div key={it.id} className="group relative bg-white border border-black/5">
                    <a href={it.source_url} target="_blank" rel="noreferrer" className="block aspect-[3/4] overflow-hidden">
                      <img src={it.image_url} alt={it.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"/>
                    </a>
                    <div className="p-4">
                      <div className="font-mono-eyebrow text-black/40">{it.site}</div>
                      <div className="font-serif-display text-lg leading-tight line-clamp-2 mt-1">{it.title}</div>
                      {it.style_tags && it.style_tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {it.style_tags.slice(0, 3).map(t => (
                            <span key={t} className="text-[9px] px-1.5 py-0.5 border border-black/15 font-mono-eyebrow capitalize">{t}</span>
                          ))}
                        </div>
                      )}
                      <div className="flex justify-between mt-3 items-center">
                        {it.price ? <span className="font-mono-eyebrow">{it.price}</span> : <span className="font-mono-eyebrow text-black/30">—</span>}
                        <div className="flex items-center gap-2">
                          {grouped[cat].length > 1 && (
                            <button
                              data-testid={`wardrobe-merge-${it.id}`}
                              onClick={() => openMerge(it)}
                              title="Merge duplicates into this piece"
                              className="text-black/40 hover:text-black transition-colors"
                            >
                              <Combine className="w-3.5 h-3.5"/>
                            </button>
                          )}
                          <button data-testid={`wardrobe-del-${it.id}`} onClick={()=>remove(it.id)} className="text-black/40 hover:text-red-700 transition-colors">
                            <Trash2 className="w-3.5 h-3.5"/>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <AdStrip slot="wardrobe" heading="Curated by the house" subheading="Sponsored · Editor's picks" limit={6}/>

      {shotOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={closeShot}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitShot}
            className="bg-[#F6F5F1] border border-black/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative p-8 md:p-12">
            <button type="button" onClick={closeShot} className="absolute top-4 right-4 p-2 hover:bg-black/5 rounded-full z-10">
              <X className="w-4 h-4"/>
            </button>
            <div className="font-mono-eyebrow">Shop from a screenshot</div>
            <h3 className="font-serif-display text-4xl leading-none mt-2 mb-3">Add without a link.</h3>
            <p className="text-black/60 max-w-md">
              Some apps don't share URLs. Drop a screenshot of the piece —
              Muse crops the garment, tags it, and files it into your wardrobe.
            </p>

            <input ref={shotFileRef} type="file" accept="image/*" hidden onChange={onShotFile} data-testid="wardrobe-shot-file"/>
            <div className="mt-6 grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-6">
                {!shotImage ? (
                  <button type="button" onClick={() => shotFileRef.current?.click()}
                    className="w-full aspect-[3/4] border border-dashed border-black/30 flex flex-col items-center justify-center gap-3 hover:border-black transition-colors bg-white">
                    <Camera className="w-6 h-6"/>
                    <div className="font-mono-eyebrow">Choose screenshot</div>
                    <div className="text-xs text-black/40 max-w-[220px] text-center">JPG or PNG · max 4MB</div>
                  </button>
                ) : (
                  <div className="relative">
                    <div className="aspect-[3/4] overflow-hidden bg-white polaroid">
                      <img src={shotImage} alt="screenshot" className="w-full h-full object-cover"/>
                    </div>
                    <button type="button" onClick={() => shotFileRef.current?.click()}
                      className="absolute top-2 right-2 bg-white/90 backdrop-blur px-3 py-1 font-mono-eyebrow text-[10px] hover:bg-black hover:text-white transition-colors">
                      Replace
                    </button>
                  </div>
                )}
              </div>

              <div className="col-span-12 md:col-span-6 flex flex-col gap-4">
                <label className="cursor-pointer group">
                  <div className="flex items-start gap-3">
                    <input type="radio" name="shot-mode" checked={shotMode === "ai"}
                      data-testid="wardrobe-shot-mode-ai"
                      onChange={() => setShotMode("ai")}
                      className="mt-1 w-4 h-4 accent-black"/>
                    <div>
                      <div className="font-mono-eyebrow flex items-center gap-1.5">
                        <Wand2 className="w-3 h-3"/> AI analyse
                      </div>
                      <div className="text-black/60 text-sm mt-1">Muse reads the image, picks a title, category and style tags.</div>
                    </div>
                  </div>
                </label>

                <label className="cursor-pointer group">
                  <div className="flex items-start gap-3">
                    <input type="radio" name="shot-mode" checked={shotMode === "multi"}
                      data-testid="wardrobe-shot-mode-multi"
                      onChange={() => setShotMode("multi")}
                      className="mt-1 w-4 h-4 accent-black"/>
                    <div>
                      <div className="font-mono-eyebrow flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3"/> Pick individual pieces
                      </div>
                      <div className="text-black/60 text-sm mt-1">
                        Multi-item photo? Muse lists every garment — you save each one on its own so you can mix and match later.
                      </div>
                    </div>
                  </div>
                </label>

                <label className="cursor-pointer group">
                  <div className="flex items-start gap-3">
                    <input type="radio" name="shot-mode" checked={shotMode === "quick"}
                      data-testid="wardrobe-shot-mode-quick"
                      onChange={() => setShotMode("quick")}
                      className="mt-1 w-4 h-4 accent-black"/>
                    <div>
                      <div className="font-mono-eyebrow">Quick add</div>
                      <div className="text-black/60 text-sm mt-1">Save it instantly. You'll type the details yourself.</div>
                    </div>
                  </div>
                </label>

                {shotMode === "quick" && (
                  <div className="space-y-3 pt-2 border-t border-black/10">
                    <div>
                      <label className="font-mono-eyebrow text-black/50">Title (optional)</label>
                      <input data-testid="wardrobe-shot-title"
                        value={shotTitle} onChange={(e) => setShotTitle(e.target.value)}
                        placeholder="Black wool coat"
                        className="field mt-1"/>
                    </div>
                    <div>
                      <label className="font-mono-eyebrow text-black/50">Category</label>
                      <Select value={shotCategory} onValueChange={setShotCategory}>
                        <SelectTrigger data-testid="wardrobe-shot-cat" className="w-full rounded-none border-0 border-b border-black bg-transparent font-mono-eyebrow uppercase focus:ring-0 mt-1">
                          <SelectValue/>
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(c => <SelectItem key={c} value={c} className="font-mono-eyebrow">{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Multi-item picker — only visible when mode = "multi" and a screenshot is uploaded */}
            {shotMode === "multi" && shotImage && (
              <div className="mt-6 pt-6 border-t border-black/10" data-testid="wardrobe-shot-multi-panel">
                {!detected && (
                  <button type="button" onClick={runDetect} disabled={detectBusy}
                    data-testid="wardrobe-shot-detect-btn"
                    className="btn-ink flex items-center gap-2 disabled:opacity-50">
                    <Sparkles className="w-3.5 h-3.5"/>
                    {detectBusy ? "Reading the photo…" : "Detect items in this photo"}
                  </button>
                )}
                {detected && detected.length === 0 && (
                  <div className="text-black/60 text-sm">
                    Muse couldn't spot distinct pieces. Try a clearer photo or use AI analyse instead.
                  </div>
                )}
                {detected && detected.length > 0 && (
                  <>
                    {(() => {
                      const totalCount = detected.length;
                      const savedCount = Object.keys(savedLabels).filter((k) => savedLabels[k]).length;
                      const remainingCount = totalCount - savedCount;
                      return (
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                          <div className="min-w-0">
                            <div className="font-mono-eyebrow" data-testid="wardrobe-shot-detected-count">
                              {totalCount} piece{totalCount === 1 ? "" : "s"} detected
                              {savedCount > 0 && <span className="text-[color:var(--accent)] ml-2">· {savedCount} saved</span>}
                            </div>
                            <div className="text-[11px] text-black/50 mt-1" data-testid="wardrobe-shot-credit-counter">
                              {remainingCount > 0
                                ? <>Save all uses <strong>{remainingCount}×</strong> generation credit{remainingCount === 1 ? "" : "s"} (1 per piece)</>
                                : "All pieces saved"}
                            </div>
                          </div>
                          {remainingCount > 0 && (
                            <button type="button"
                              data-testid="wardrobe-shot-save-all"
                              onClick={saveAllPicked}
                              disabled={bulkBusy || !!savingLabel}
                              className="btn-ink flex items-center gap-2 disabled:opacity-50">
                              <Sparkles className="w-3.5 h-3.5"/>
                              {bulkBusy
                                ? `Saving ${(savedCount + 1)} / ${totalCount}…`
                                : `Save all ${remainingCount} piece${remainingCount === 1 ? "" : "s"}`}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {detected.map((it, idx) => {
                        const saved = !!savedLabels[it.label];
                        const saving = savingLabel === it.label;
                        return (
                          <div key={it.label}
                            data-testid={`wardrobe-shot-detected-${idx}`}
                            className={`border p-3 flex items-center justify-between gap-3 ${saved ? "border-[color:var(--accent)] bg-[color:var(--accent)]/5" : "border-black/15 bg-white"}`}>
                            <div className="min-w-0">
                              <div className="text-sm leading-tight truncate">{it.label}</div>
                              <div className="font-mono-eyebrow text-[10px] text-black/50 mt-1 uppercase">{it.category}</div>
                            </div>
                            <button type="button"
                              data-testid={`wardrobe-shot-save-${idx}`}
                              onClick={() => savePickedItem(it.label, it.category)}
                              disabled={saved || saving || !!savingLabel}
                              className={`shrink-0 font-mono-eyebrow px-3 py-2 text-[10px] transition-colors ${saved ? "bg-[color:var(--accent)] text-white cursor-default" : "border border-black hover:bg-black hover:text-white disabled:opacity-40"}`}>
                              {saved ? "Saved ✓" : saving ? "Isolating…" : "Save this piece"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-black/10">
              <button type="button" onClick={closeShot} className="btn-outline-ink">
                {shotMode === "multi" && Object.keys(savedLabels).length > 0 ? "Done" : "Cancel"}
              </button>
              {shotMode !== "multi" && (
                <button data-testid="wardrobe-shot-submit" type="submit" disabled={shotBusy || !shotImage}
                  className="btn-ink disabled:opacity-50">
                  {shotBusy ? (shotMode === "ai" ? "Analysing…" : "Saving…") : (shotMode === "ai" ? "Analyse & save" : "Save")}
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {mergeKeepItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={closeMerge}>
          <div onClick={(e) => e.stopPropagation()} data-testid="wardrobe-merge-modal"
            className="bg-[#F6F5F1] border border-black/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto relative p-8 md:p-12">
            <button onClick={closeMerge} className="absolute top-4 right-4 text-black/50 hover:text-black" data-testid="wardrobe-merge-close">
              <X className="w-5 h-5"/>
            </button>
            <div className="font-mono-eyebrow text-black/50">Merge duplicates</div>
            <h2 className="font-serif-display text-3xl mt-1">Fold near-identical pieces into one</h2>

            <div className="mt-6 pb-6 border-b border-black/10 flex gap-4 items-center">
              <div className="w-16 h-20 shrink-0 overflow-hidden border border-black/10 bg-white">
                <img src={mergeKeepItem.image_url} alt="keep" className="w-full h-full object-cover"/>
              </div>
              <div className="min-w-0">
                <div className="font-mono-eyebrow text-[10px] text-[color:var(--accent)]">Keep this piece</div>
                <div className="font-serif-display text-lg leading-tight truncate mt-0.5">{mergeKeepItem.title}</div>
                <div className="text-[11px] text-black/50 mt-1 uppercase font-mono-eyebrow">{mergeKeepItem.category}</div>
              </div>
            </div>

            <div className="mt-6">
              <div className="font-mono-eyebrow text-black/50 mb-3">
                Tick every piece in your <strong className="text-black">{mergeKeepItem.category}s</strong> that is really this same garment — they'll be deleted, and every outfit or try-on that referenced them will point to the kept piece.
              </div>
              {(() => {
                const siblings = (items || []).filter(
                  (x) => x.category === mergeKeepItem.category && x.id !== mergeKeepItem.id
                );
                if (siblings.length === 0) {
                  return <div className="text-black/50 text-sm py-6 text-center">No other {mergeKeepItem.category}s in your wardrobe yet.</div>;
                }
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {siblings.map((it) => {
                      const picked = !!mergePicked[it.id];
                      return (
                        <label key={it.id}
                          data-testid={`wardrobe-merge-pick-${it.id}`}
                          className={`relative cursor-pointer border transition-colors ${picked ? "border-[color:var(--accent)] ring-2 ring-[color:var(--accent)]" : "border-black/15 hover:border-black"}`}>
                          <input type="checkbox" checked={picked}
                            onChange={() => toggleMergePick(it.id)}
                            className="absolute top-2 right-2 w-4 h-4 accent-black z-10"/>
                          <div className="aspect-[3/4] overflow-hidden bg-white">
                            <img src={it.image_url} alt={it.title} className="w-full h-full object-cover"/>
                          </div>
                          <div className="p-2">
                            <div className="text-[11px] leading-tight line-clamp-2">{it.title}</div>
                            <div className="font-mono-eyebrow text-[9px] text-black/40 mt-1">{it.site}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-black/10">
              <button type="button" onClick={closeMerge} className="btn-outline-ink">Cancel</button>
              <button
                data-testid="wardrobe-merge-confirm"
                onClick={confirmMerge}
                disabled={mergeBusy || Object.values(mergePicked).filter(Boolean).length === 0}
                className="btn-ink disabled:opacity-40">
                {mergeBusy ? "Merging…" : `Merge ${Object.values(mergePicked).filter(Boolean).length || ""} into this piece`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wardrobe;
